<script setup lang="ts">
import { IconExclamationCircle } from '@tabler/icons-vue'
import { useRouter } from 'vue-router'

import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'

import { isDonationCart, trackDonationPurchaseFromCart } from '@/helpers/donationGa4'
import { type PaymentReturnQuery, buildCompletedConfirmationPath, settlePaymentReturn } from '@/helpers/paymentReturn'

import { errorTracker } from '@/services/errorTracking'

import LoadingSpinner from '@/components/LoadingSpinner.vue'

const props = defineProps<
  PaymentReturnQuery & {
    confirmationPath: string
    retryPath?: string
  }
>()

const router = useRouter()

const result = await settlePaymentReturn({
  cartId: props.cartId,
  purchaserId: props.purchaserId,
  sourceType: props.sourceType,
  clientSecret: props.clientSecret,
})

if (result.status === 'completed') {
  if (isDonationCart(result.cart)) {
    trackDonationPurchaseFromCart(result.cart, result.orderNumber)
  }

  await router.replace(
    buildCompletedConfirmationPath({
      confirmationPath: props.confirmationPath,
      orderNumber: result.orderNumber,
      amountMinor: result.amountMinor,
    }),
  )
} else {
  errorTracker.track(new Error(result.message), {
    operation: 'payment_return.settle',
    status: result.status,
    cartId: props.cartId,
  })
}

function retry() {
  if (props.retryPath) router.replace(props.retryPath)
}
</script>

<template>
  <StatusMessage v-if="result.status === 'completed'" class="redirecting-message" variant="success">
    <LoadingSpinner :size="16" />
    Payment completed, redirecting to order confirmation...
  </StatusMessage>

  <StatusMessage
    v-else
    :buttonLabel="retryPath ? 'Retry' : undefined"
    :dismissible="false"
    :icon="IconExclamationCircle"
    :message="result.message"
    variant="error"
    @button-click="retry"
  />
</template>

<style scoped>
.redirecting-message :deep(.message-body) {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}
</style>
